import React, { useRef } from 'react';
import Table from '../../components/Table/Table';
import ProductForm from '../../components/Form/ProductForm';
import { Product, InventoryCategory } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

interface ProductsTabProps {
  products: Product[];
  categories: InventoryCategory[];
  filteredProducts: Product[];
  showProductForm: boolean;
  setShowProductForm: (show: boolean) => void;
  editingProduct: Product | undefined;
  setEditingProduct: (product: Product | undefined) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  categoryFilter: string;
  setCategoryFilter: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  stockLevelFilter: string;
  setStockLevelFilter: (value: string) => void;
  currencyFilter: string;
  setCurrencyFilter: (value: string) => void;
  onAddProduct: (data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onEditProduct: (data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onDeleteProduct: (id: string) => void;
  onEditProductClick: (product: Product) => void;
  onExport: () => void;
  onPrint: () => void;
  onImport: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearFilters: () => void;
}

const ProductsTab: React.FC<ProductsTabProps> = ({
  products, categories, filteredProducts,
  showProductForm, setShowProductForm,
  editingProduct, setEditingProduct,
  searchTerm, setSearchTerm,
  showFilters, setShowFilters,
  categoryFilter, setCategoryFilter,
  statusFilter, setStatusFilter,
  stockLevelFilter, setStockLevelFilter,
  currencyFilter, setCurrencyFilter,
  onAddProduct, onEditProduct, onDeleteProduct, onEditProductClick,
  onExport, onPrint, onImport, onClearFilters,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    onImport(event);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const productColumns = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (value: string) => <span className="font-semibold text-gray-900">{value}</span>,
    },
    { key: 'name', label: 'Product Name', sortable: true },
    {
      key: 'categoryId',
      label: 'Category',
      render: (value: string) => {
        const category = categories.find(c => c.documentId === value || String(c.id) === String(value));
        return category ? category.name : '-';
      },
    },
    {
      key: 'quantity',
      label: 'Quantity',
      sortable: true,
      render: (value: number, item: Product) => (
        <span className={`font-medium ${
          value <= item.minStockLevel ? 'text-red-600' :
          value >= item.maxStockLevel ? 'text-green-600' :
          'text-gray-900'
        }`}>
          {value} {item.unit}
        </span>
      ),
    },
    {
      key: 'unitPrice',
      label: 'Unit Price',
      sortable: true,
      render: (value: number, item: Product) => (
        <span className="font-medium text-gray-900">
          {value.toFixed(2)} {resolveCurrencyCode(item.currency)}
        </span>
      ),
    },
    { key: 'supplier', label: 'Supplier', sortable: true },
    { key: 'location', label: 'Location' },
    {
      key: 'isActive',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, item: Product) => (
        <div className="flex space-x-2">
          <button onClick={() => onEditProductClick(item)} className="text-blue-600 hover:text-blue-900 font-medium">Edit</button>
          <button onClick={() => onDeleteProduct(item.id)} className="text-red-600 hover:text-red-900 font-medium">Delete</button>
        </div>
      ),
    },
  ];

  if (showProductForm) {
    return (
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">
          {editingProduct ? 'Edit Product' : 'Add New Product'}
        </h2>
        <ProductForm
          onSubmit={editingProduct ? onEditProduct : onAddProduct}
          initialData={editingProduct}
          onCancel={() => { setShowProductForm(false); setEditingProduct(undefined); }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-sm text-gray-600">Total Products</div>
          <div className="text-2xl font-bold text-gray-900">{products.length}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Active Products</div>
          <div className="text-2xl font-bold text-green-600">{products.filter(p => p.isActive).length}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Low Stock Items</div>
          <div className="text-2xl font-bold text-red-600">{products.filter(p => p.quantity <= p.minStockLevel).length}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Total Value (EGP)</div>
          <div className="text-2xl font-bold text-gray-900">
            {products.filter(p => resolveCurrencyCode(p.currency) === 'EGP')
              .reduce((sum, p) => sum + (p.quantity * p.unitPrice), 0)
              .toLocaleString()}
          </div>
        </div>
      </div>

      <div className="card p-4 space-y-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full lg:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
          />
          <div className="flex flex-wrap gap-2 w-full lg:w-auto">
            <button onClick={() => setShowFilters(!showFilters)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ocean-500">
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </button>
            <button onClick={onExport} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ocean-500">
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
            <button onClick={onPrint} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ocean-500">
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            <label className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ocean-500 cursor-pointer">
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Import
              <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImport} className="hidden" />
            </label>
            <button onClick={() => setShowProductForm(true)} className="px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500">
              + Add Product
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent">
                <option value="">All Categories</option>
                {categories.map(cat => <option key={cat.id} value={cat.documentId || cat.id}>{cat.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock Level</label>
              <select value={stockLevelFilter} onChange={(e) => setStockLevelFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent">
                <option value="">All Levels</option>
                <option value="low">Low Stock</option>
                <option value="normal">Normal Stock</option>
                <option value="high">High Stock</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent">
                <option value="">All Currencies</option>
                <option value="EGP">EGP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <button onClick={onClearFilters} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        <Table data={filteredProducts} columns={productColumns} />
      </div>
    </>
  );
};

export default ProductsTab;
